import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="variation-preview", around the variation form and its preview card. Fills the card
// from the form's fields as the author types, with the text the parent's page shows: the same joins as
// Monster#full_dv, Monster#movements and MonsterAttack#to_s.
export default class extends Controller<HTMLElement> {
	static targets = ["title", "sizeAlignment", "xp", "dv", "ca", "jp", "movement", "mo", "attacks", "abilities"];
	static values = { xpAbbr: String, movementLabels: Object };
	declare readonly titleTarget: HTMLElement;
	declare readonly sizeAlignmentTarget: HTMLElement;
	declare readonly xpTarget: HTMLElement;
	declare readonly dvTarget: HTMLElement;
	declare readonly caTarget: HTMLElement;
	declare readonly jpTarget: HTMLElement;
	declare readonly movementTarget: HTMLElement;
	declare readonly moTarget: HTMLElement;
	declare readonly attacksTarget: HTMLElement;
	declare readonly abilitiesTarget: HTMLElement;
	declare readonly xpAbbrValue: string;
	declare readonly movementLabelsValue: Record<string, string>;

	connect() {
		this.update();
		// Each attack row fills its own "Na ficha" text when its controller connects, after this one.
		requestAnimationFrame(() => this.update());
	}

	update() {
		const name = this.field("name").trim();
		this.titleTarget.textContent = name || (this.titleTarget.dataset.empty ?? "");
		this.sizeAlignmentTarget.textContent = `${this.initial("size")} – ${this.initial("alignment")}`;
		this.xpTarget.textContent = `[${this.field("xp")}${this.xpAbbrValue}]`;
		this.dvTarget.textContent = this.fullDv();
		this.caTarget.textContent = this.field("ca");
		this.jpTarget.textContent = this.field("jp");
		this.movementTarget.textContent = this.movements();
		this.moTarget.textContent = this.field("mo");
		this.attacksTarget.replaceChildren(...this.attackLines().map((line) => this.line("div", line)));
		this.abilitiesTarget.replaceChildren(...this.abilityLines().map((line) => this.line("li", line)));
	}

	private input(name: string) {
		return this.element.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="monster[${name}]"]`);
	}

	private field(name: string) {
		return this.input(name)?.value ?? "";
	}

	private initial(name: string) {
		const select = this.input(name) as HTMLSelectElement | null;
		return select?.selectedOptions[0]?.text.charAt(0) ?? "";
	}

	private line(tag: string, text: string) {
		const element = document.createElement(tag);
		element.textContent = text;
		return element;
	}

	// "1½+5 [10]": a DV of 0 reads ½, and the bonus is joined as typed.
	private fullDv() {
		const dv = this.field("dv");
		return `${dv === "0" ? "½" : dv}${this.field("dv_bonus")} [${this.field("pv")}]`;
	}

	private movements() {
		const parts = [this.field("mv")];
		for (const [name, label] of Object.entries(this.movementLabelsValue)) {
			const value = this.field(name);
			if (value) parts.push(`${value}${label}`);
		}
		return parts.filter(Boolean).join(", ");
	}

	private attackLines() {
		const rows = this.element.querySelectorAll<HTMLElement>("[data-controller~='monster-attack-preview']");
		return [...rows]
			.filter((row) => row.style.display !== "none")
			.map((row) => {
				const value = (name: string) => row.querySelector<HTMLInputElement>(`[name$="[${name}]"]`)?.value ?? "";
				const ba = Number.parseInt(value("ba"), 10);
				const signedBa = Number.isFinite(ba) && ba !== 0 ? (ba > 0 ? `+${ba}` : `${ba}`) : "";
				const description = [value("description"), signedBa].filter(Boolean).join(" ");
				const damage = row.querySelector("[data-monster-attack-preview-target='preview']")?.textContent ?? "";
				return `${value("times")} × ${description} (${damage})`;
			});
	}

	private abilityLines() {
		const fields = this.element.querySelectorAll<HTMLInputElement>("input[name='monster[modifications][]']");
		return [...fields].map((field) => field.value.trim()).filter(Boolean);
	}
}
